import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { useEventBus } from "shared/services/eventBus";
import {
  CONNECTION_EVENTS,
  ROBOT_EVENTS,
  getRobotEvent
} from "shared/services/eventBus/eventTypes";
import { CONNECTION_STATES } from "shared/utils/stateMachine/ConnectionStateMachine";
import { useServerConnection } from "shared/contexts/ServerConnectionContext";
import { robotPeerConnectionsService } from "shared/services/robot-services/robotPeerConnectionsService";
import { useAuth } from "shared/contexts/AuthProvider";
import logger from "shared/utils/logging";

/**
 * RobotSignalingContext - Manages WebRTC signaling for robot connections
 *
 * Responsibilities:
 * - Subscribe to server connection state changes
 * - Handle SDP (Session Description Protocol) signaling
 * - Handle ICE (Interactive Connectivity Establishment) candidates
 * - Manage robot online/offline status events
 * - Publish robot-specific signaling events via EventBus
 *
 * This context decouples signaling logic from WebRTC peer connection management,
 * making the code more maintainable and testable.
 */

const RobotPeerSignalingContext = createContext();

export const RobotPeerSignalingProvider = ({ children, RobotPeerId }) => {
  const eventBus = useEventBus();
  const { authFetch } = useAuth();
  
  const { connectionState, isConnecting, error, sendMessage, sendRequestMessage } = useServerConnection();  

  // Track server connection readiness
  const [isServerConnected, setIsServerConnected] = useState(false);

  // Track robot online status
  const [isRobotPeerOnline, setIsRobotPeerOnline] = useState(false);

  // Track signaling readiness (both server connected AND robot online)
  const [isSignalingReady, setIsSignalingReady] = useState(false);

  // Store latest SDP and ICE candidate for reconnection scenarios
  const latestSDPRef = useRef(null);
  const pendingICECandidatesRef = useRef([]);

  /**
   * Handle SDP (Session Description Protocol) from robot
   * @param {Object} sdp - SDP object with type and sdp properties
   */
  const handleSignalSDP = (sdp) => {
    logger.log(`[RobotSignaling] Received SDP for robot ${RobotPeerId}:`, sdp);
    latestSDPRef.current = sdp;

    // // Publish SDP event for this specific robot
    // eventBus.publish(getRobotEvent(RobotPeerId, ROBOT_EVENTS.SIGNAL_SDP), sdp);
  };

  /**
   * Handle ICE candidate from robot
   * @param {Object} signal_data - Signal data with RobotId and IceCandidate
   */
  const handleSignalCandidate = (signal_data) => {
    logger.log(`[RobotSignaling] Received ICE candidate for robot ${RobotPeerId}:`, signal_data);

    const { IceCandidate } = signal_data;

    if (IceCandidate) {
      pendingICECandidatesRef.current.push(IceCandidate);
    }

    // Publish ICE candidate event for this specific robot
    // eventBus.publish(
    //   getRobotEvent(RobotPeerId, ROBOT_EVENTS.SIGNAL_CANDIDATE),
    //   signal_data
    // );
  };

  /**
   * Handle robot connected event
   * @param {Object} evt - Robot connected event data
   */
  const handleRobotConnected = (evt) => {
    logger.log(`[RobotSignaling] Robot ${RobotPeerId} connected:`, evt);
    setIsRobotPeerOnline(true);

    // // Publish robot connected event
    // eventBus.publish(
    //   getRobotEvent(RobotPeerId, ROBOT_EVENTS.CONNECTED),
    //   evt
    // );
  };

  /**
   * Handle robot disconnected event
   * @param {Object} evt - Robot disconnected event data
   */
  const handleRobotDisconnected = (evt) => {
    logger.log(`[RobotSignaling] Robot ${RobotPeerId} disconnected:`, evt);
    setIsRobotPeerOnline(false);

    // Clear cached signaling data
    latestSDPRef.current = null;
    pendingICECandidatesRef.current = [];

    // // Publish robot disconnected event
    // eventBus.publish(
    //   getRobotEvent(RobotPeerId, ROBOT_EVENTS.DISCONNECTED),
    //   evt
    // );
  };

  /**
   * Subscribe to server connection state changes via EventBus
   * Replaces the signal_ready flag from ServerConnectionContext
   */
  useEffect(() => {
    logger.log(`[RobotSignaling] Subscribing to server connection state changes`);
    
    // Subscribe to connection state changes
    const unsubscribe = eventBus.subscribe(
      CONNECTION_EVENTS.SERVER_STATE_CHANGED,
      ({ state }) => {
        const isConnected = state === CONNECTION_STATES.CONNECTED;
        logger.log(`[RobotSignaling] Server connection state: ${state}, connected: ${isConnected}`);
        setIsServerConnected(isConnected);
      }
    );

    // Also check current connection state
    const isConnected = connectionState === CONNECTION_STATES.CONNECTED;
    setIsServerConnected(isConnected);

    return () => {
      unsubscribe();
    };
  }, [eventBus, connectionState]);

  /**
   * Subscribe to robot signaling events (SDP, ICE candidates, online/offline)
   * Uses legacy subscribe from ServerConnectionContext for backward compatibility
   */
  useEffect(() => {
    if (!RobotPeerId) {
      logger.warn('[RobotSignaling] No robotId provided');
      return;
    }

    robotPeerConnectionsService.getPeerInfoByPeerId(authFetch, RobotPeerId)
      .then(async (response) => {
        if (response && response.data) {
          const robotPeerInfo = response.data;          
          logger.log(`Robot peer info for robot_id=${RobotPeerId}:`, robotPeerInfo);
          if (robotPeerInfo && robotPeerInfo?.status) {
            const peerStatus = robotPeerInfo.status;
            setIsRobotPeerOnline(peerStatus === "online");            
          }else {
            throw new Error(
              `Robot peer info for robot_id=${RobotPeerId} does not contain status field.`
            );
          }          
        } else {
          throw new Error(
            `Cannot fetch robot peer connection info for robot_id=${RobotPeerId}: response was not ok.`
          );
        }
      })
      .catch((error) => {
        logger.error(`[RobotSignaling] Error fetching robot peer status for robot_id=${RobotPeerId}:`, error);
      });        

    logger.log(`[RobotSignaling] Setting up signaling subscriptions for robot ${RobotPeerId}`);
        

    // Subscribe to robot connected/disconnected events
    const unsubscribe_connected = eventBus.subscribe(
      getRobotEvent(RobotPeerId, ROBOT_EVENTS.CONNECTED),
      handleRobotConnected
    );

    const unsubscribe_disconnected = eventBus.subscribe(
      getRobotEvent(RobotPeerId, ROBOT_EVENTS.DISCONNECTED),
      handleRobotDisconnected
    );

    // Subscribe to SDP signaling
    const unsubscribe_signal_sdp = eventBus.subscribe(
      getRobotEvent(RobotPeerId, ROBOT_EVENTS.SIGNAL_SDP),
      handleSignalSDP
    );

    // Subscribe to ICE candidate signaling
    const unsubscribe_signal_candidate = eventBus.subscribe(
      getRobotEvent(RobotPeerId, ROBOT_EVENTS.SIGNAL_CANDIDATE),
      handleSignalCandidate
    );

    return () => {
      logger.log(`[RobotSignaling] Cleaning up signaling subscriptions for robot ${RobotPeerId}`);
      unsubscribe_connected();
      unsubscribe_disconnected();
      unsubscribe_signal_sdp();
      unsubscribe_signal_candidate();

      // Clear cached data
      latestSDPRef.current = null;
      pendingICECandidatesRef.current = [];
    };
  }, [RobotPeerId, eventBus]);

  /**
   * Update signaling readiness based on server connection and robot online status
   */
  useEffect(() => {
    const ready = isServerConnected && isRobotPeerOnline;
    logger.log(
      `[RobotSignaling] Signaling ready: ${ready} (server: ${isServerConnected}, robot: ${isRobotPeerOnline})`
    );
    setIsSignalingReady(ready);

    // Publish signaling ready event
    if (ready) {
      eventBus.publish(
        getRobotEvent(RobotPeerId, ROBOT_EVENTS.SIGNALING_READY),
        { robotId: RobotPeerId, timestamp: Date.now() }
      );
    }
  }, [isServerConnected, isRobotPeerOnline, RobotPeerId, eventBus]);

  const value = {
    // State
    isServerConnected,
    isRobotOnline: isRobotPeerOnline,
    isSignalingReady,
    robotId: RobotPeerId,
    sendSignalMessage: sendMessage,
    sendSignalRequestMessage: sendRequestMessage,
    // Cached signaling data
    latestSDP: latestSDPRef.current,
    pendingICECandidates: pendingICECandidatesRef.current,

    // Methods (for advanced use cases)
    clearSignalingCache: () => {
      latestSDPRef.current = null;
      pendingICECandidatesRef.current = [];
    },
  };

  return (
    <RobotPeerSignalingContext.Provider value={value}>
      {children}
    </RobotPeerSignalingContext.Provider>
  );
};

/**
 * Hook to access robot signaling context
 * @returns {Object} Robot signaling context value
 */
export const useRobotSignaling = () => {
  const context = useContext(RobotPeerSignalingContext);
  if (!context) {
    throw new Error('useRobotSignaling must be used within RobotSignalingProvider');
  }
  return context;
};

export default RobotPeerSignalingContext;
